import { useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  createCroppedImageFile,
  getImageDimensions,
  normalizeImageFileForUpload,
} from '~/components/Common/Images/cropUtils';

import { MAX_SVC_IMAGE_UPLOAD_BYTES, getUploadedImageId, uploadImage } from '~/services/ImageService';
import OffersService from '~/services/OffersService';
import ReservationService from '~/services/ReservationService';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import {
  type LocalImageEdit,
  buildDefaultEdit,
  getImageCropPixels,
  hasImagePixelEdit,
} from './ImagesVideo/ImageEditor/ImageEditorUtils';
import type { RoomImageOrderOverrides } from './ImagesVideo/ImagesByRoom/utils';
import type { MediaImage, PropertyRoomTypeWithImages } from './ImagesVideo/types';
import {
  allSettledWithConcurrency,
  buildMediaImages,
  buildOfferImageOrderUpdatePayload,
  buildRoomImageOrderUpdatePayload,
  buildUploadPlans,
  canPersistRoomImageHiddenState,
  dedupeMediaImages,
  getMediaImageSourceKey,
  getPrimaryVideoId,
  getRoomKey,
  isAllowedImageFile,
} from './ImagesVideo/utils';
import type { UploadRoomAssignment, UploadedRoomAssignment } from './ImagesVideo/utils';

const IMAGE_UPLOAD_CONCURRENCY = 5;

interface UseOfferBuilderMediaParams {
  offerId: string;
  images?: Array<App.OfferImage>;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  videos?: {
    luxuryescapes?: {
      vimeo_id?: string | null;
    };
  };
}

function removeImageOverride(overrides: Record<string, boolean>, publicId: string) {
  const { [publicId]: _removedOverride, ...nextOverrides } = overrides;

  return nextOverrides;
}

interface UploadFilesOptions {
  ignoreNonImageFiles?: boolean;
  roomAssignment?: UploadRoomAssignment;
  skipRoomMatching?: boolean;
}

export interface OfferBuilderMediaController {
  deletedImagePublicIds: Array<string>;
  hiddenImagePublicIds: Array<string>;
  imageEdits: Record<string, LocalImageEdit>;
  imagePublicIdReplacements: Record<string, string>;
  isUploadingImages: boolean;
  mediaImages: Array<MediaImage>;
  roomImagePendingDeletion?: MediaImage;
  roomImageOrderOverrides: RoomImageOrderOverrides;
  roomImagePendingEdit?: MediaImage;
  uploadedRoomAssignments: Array<UploadedRoomAssignment>;
  videoId?: string;
  closeRoomImageDeleteDialog: () => void;
  confirmRemoveRoomImage: () => Promise<void>;
  deleteImage: (publicId: string) => Promise<void>;
  removeRoomImageFromEditor: (image: MediaImage) => void;
  removeImageFromRoom: (image: MediaImage) => Promise<void>;
  reorderOfferGalleryImages: (orderedPublicIds: Array<string>) => Promise<void>;
  reorderRoomImages: (roomKey: string, orderedPublicIds: Array<string>) => Promise<void>;
  saveRoomImageTitleEdits: (roomKey: string) => Promise<void>;
  saveImageEdit: (publicId: string, edit: LocalImageEdit) => Promise<void>;
  setRoomImagePendingEdit: (image?: MediaImage) => void;
  setVideoId: (videoId?: string) => void;
  toggleHiddenImage: (publicId: string) => Promise<void>;
  updateImageTitle: (roomKey: string, publicId: string, title: string) => void;
  uploadFiles: (files: Array<File>, options?: UploadFilesOptions) => Promise<void>;
  uploadFilesToOfferGallery: (files: Array<File>) => Promise<void>;
  uploadFilesToRoom: (roomId: string, roomName: string, files: Array<File>) => Promise<void>;
  editRoomImage: (publicId: string) => void;
}

export default function useOfferBuilderMedia({
  offerId,
  images = [],
  propertyRoomTypes,
  videos,
}: UseOfferBuilderMediaParams): OfferBuilderMediaController {
  const { enqueueSnackbar } = useSnackbar();
  const [uploadedImages, setUploadedImages] = useState<Array<MediaImage>>([]);
  const [uploadedRoomAssignments, setUploadedRoomAssignments] = useState<Array<UploadedRoomAssignment>>([]);
  const [deletedImagePublicIds, setDeletedImagePublicIds] = useState<Array<string>>([]);
  const [deletedImageSourceKeys, setDeletedImageSourceKeys] = useState<Array<string>>([]);
  const [removedRoomImageSourceKeys, setRemovedRoomImageSourceKeys] = useState<Array<string>>([]);
  const [replacedImageSourceKeys, setReplacedImageSourceKeys] = useState<Array<string>>([]);
  const [imagePublicIdReplacements, setImagePublicIdReplacements] = useState<Record<string, string>>({});
  const [hiddenImageOverrides, setHiddenImageOverrides] = useState<Record<string, boolean>>({});
  const [roomImagePendingDeletion, setRoomImagePendingDeletion] = useState<MediaImage | undefined>();
  const [roomImageOrderOverrides, setRoomImageOrderOverrides] = useState<RoomImageOrderOverrides>({});
  const [roomImagePendingEdit, setRoomImagePendingEdit] = useState<MediaImage | undefined>();
  const [imageEdits, setImageEdits] = useState<Record<string, LocalImageEdit>>({});
  const [savedImageTitles, setSavedImageTitles] = useState<Record<string, string>>({});
  const [localVideoId, setLocalVideoId] = useState<string | undefined>();
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const hiddenImageUpdatePublicIdsRef = useRef<Set<string>>(new Set());
  const roomImageDeletionPromisesRef = useRef<Map<string, Promise<void>>>(new Map());
  const videoId = localVideoId === '' ? undefined : (localVideoId ?? getPrimaryVideoId(videos));

  const mediaImages = useMemo(() => {
    const deletedImageSourceKeySet = new Set(deletedImageSourceKeys);
    const removedRoomImageSourceKeySet = new Set(removedRoomImageSourceKeys);
    const replacedImageSourceKeySet = new Set(replacedImageSourceKeys);

    return dedupeMediaImages(
      buildMediaImages({
        offerImages: images,
        propertyRoomTypes,
      }).concat(uploadedImages),
    )
      .filter((image) => !deletedImageSourceKeySet.has(getMediaImageSourceKey(image)))
      .filter((image) => !replacedImageSourceKeySet.has(getMediaImageSourceKey(image)))
      .map((image) => {
        const imageSourceKey = getMediaImageSourceKey(image);
        const imageEdit = imageEdits[imageSourceKey] ?? imageEdits[image.publicId];
        const isRemovedRoomImage = removedRoomImageSourceKeySet.has(imageSourceKey);

        return {
          ...image,
          hidden: isRemovedRoomImage ? false : image.hidden,
          sourceLabel: image.sourceLabel,
          sourceImageId: isRemovedRoomImage ? undefined : image.sourceImageId,
          sourcePropertyId: isRemovedRoomImage ? undefined : image.sourcePropertyId,
          sourceRoomId: isRemovedRoomImage ? undefined : image.sourceRoomId,
          sourceRoomTypeId: isRemovedRoomImage ? undefined : image.sourceRoomTypeId,
          sourceType: isRemovedRoomImage ? undefined : image.sourceType,
          title: imageEdit?.title ?? savedImageTitles[imageSourceKey] ?? image.title,
        };
      });
  }, [
    deletedImageSourceKeys,
    imageEdits,
    images,
    propertyRoomTypes,
    removedRoomImageSourceKeys,
    replacedImageSourceKeys,
    savedImageTitles,
    uploadedImages,
  ]);

  const hiddenImagePublicIds = useMemo(
    () =>
      mediaImages
        .filter((image) => hiddenImageOverrides[image.publicId] ?? image.hidden)
        .map((image) => image.publicId),
    [hiddenImageOverrides, mediaImages],
  );

  const uploadFiles = async (files: Array<File>, options: UploadFilesOptions = {}) => {
    const candidateFiles = options.ignoreNonImageFiles ? files.filter(isAllowedImageFile) : files;
    const uploadableFiles = candidateFiles.filter(
      (file) => isAllowedImageFile(file) && file.size <= MAX_SVC_IMAGE_UPLOAD_BYTES,
    );
    const rejectedFileCount = candidateFiles.length - uploadableFiles.length;

    if (rejectedFileCount > 0) {
      enqueueSnackbar(
        `${rejectedFileCount} ${
          rejectedFileCount === 1 ? 'file was' : 'files were'
        } skipped. Use supported images under 5MB.`,
        { variant: 'warning' },
      );
    }

    if (uploadableFiles.length === 0) {
      return;
    }

    setIsUploadingImages(true);

    try {
      const uploadPlans = buildUploadPlans({
        files: uploadableFiles,
        propertyRoomTypes: options.skipRoomMatching ? undefined : propertyRoomTypes,
        roomAssignment: options.roomAssignment,
      }).map((uploadPlan, index) => ({ ...uploadPlan, uploadIndex: index }));

      const uploadResults = await allSettledWithConcurrency(
        uploadPlans,
        IMAGE_UPLOAD_CONCURRENCY,
        async ({
          file,
          roomAssignment,
          uploadIndex,
        }): Promise<{ image: MediaImage; roomAssignment?: UploadedRoomAssignment }> => {
          const response = await uploadImage(file);
          const uploadedImageId = getUploadedImageId(response);

          if (!uploadedImageId) {
            throw new Error('Image service did not return an image id');
          }

          let persistedOfferImage;
          let persistedRoomImage: Awaited<ReturnType<typeof createPersistedRoomImage>> | undefined;

          if (roomAssignment) {
            try {
              persistedRoomImage = await createPersistedRoomImage({
                propertyRoomTypes,
                publicId: uploadedImageId,
                roomAssignment,
                uploadIndex,
              });
            } catch (error) {
              if (error instanceof Error) {
                reportError(error);
              }
            }
          } else {
            persistedOfferImage = await OffersService.createImage(
              {
                filename: file.name,
                id_cloudinary_external: uploadedImageId,
              },
              offerId,
            );
          }

          return {
            image: {
              hidden: persistedRoomImage?.hidden ?? persistedOfferImage?.hidden,
              order: persistedRoomImage?.order ?? persistedOfferImage?.order,
              publicId: persistedOfferImage?.id_cloudinary_external ?? uploadedImageId,
              sourceLabel: roomAssignment?.roomName ?? 'Offer gallery',
              sourceImageId:
                persistedRoomImage?.id ??
                (persistedOfferImage ? String(persistedOfferImage.id ?? persistedOfferImage.id_image) : undefined),
              sourcePropertyId: persistedRoomImage?.sourcePropertyId,
              sourceRoomId: roomAssignment?.roomId,
              sourceRoomTypeId: persistedRoomImage?.sourceRoomTypeId,
              sourceType: roomAssignment ? 'room' : 'offer',
              title: persistedRoomImage?.title || persistedOfferImage?.title || undefined,
            },
            roomAssignment: roomAssignment
              ? {
                  ...roomAssignment,
                  publicId: uploadedImageId,
                }
              : undefined,
          };
        },
      );
      const uploadedMediaImages: Array<MediaImage> = [];
      const newUploadedRoomAssignments: Array<UploadedRoomAssignment> = [];

      uploadResults.forEach((result) => {
        if (result.status === 'fulfilled') {
          uploadedMediaImages.push(result.value.image);
          if (result.value.roomAssignment) {
            newUploadedRoomAssignments.push(result.value.roomAssignment);
          }
          return;
        }

        if (result.reason instanceof Error) {
          reportError(result.reason);
        }
      });

      const failedUploadCount = uploadResults.length - uploadedMediaImages.length;

      if (uploadedMediaImages.length > 0) {
        setUploadedImages((currentImages) => dedupeMediaImages(currentImages.concat(uploadedMediaImages)));
        setUploadedRoomAssignments((currentAssignments) => currentAssignments.concat(newUploadedRoomAssignments));
        enqueueSnackbar(
          `${uploadedMediaImages.length} ${uploadedMediaImages.length === 1 ? 'image' : 'images'} uploaded`,
          { variant: 'success' },
        );
      }

      if (failedUploadCount > 0) {
        enqueueSnackbar(`${failedUploadCount} ${failedUploadCount === 1 ? 'image' : 'images'} failed to upload`, {
          variant: 'error',
        });
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'Failed to upload images'), { variant: 'error' });
      if (error instanceof Error) {
        reportError(error);
      }
    } finally {
      setIsUploadingImages(false);
    }
  };

  const uploadFilesToOfferGallery = (files: Array<File>) => uploadFiles(files, { skipRoomMatching: true });

  const uploadFilesToRoom = (roomId: string, roomName: string, files: Array<File>) =>
    uploadFiles(files, { roomAssignment: { roomId, roomName } });

  const reorderOfferGalleryImages = async (orderedPublicIds: Array<string>) => {
    const persistedUploadedOfferImages = uploadedImages.flatMap((image) => {
      if (image.sourceType !== 'offer' || !image.sourceImageId) {
        return [];
      }

      return [
        {
          id_image: Number(image.sourceImageId),
          id_cloudinary_external: image.publicId,
        } as App.OfferImage,
      ];
    });
    const updatePayload = buildOfferImageOrderUpdatePayload(
      images.concat(persistedUploadedOfferImages),
      orderedPublicIds,
    );

    if (updatePayload.length === 0) {
      return;
    }

    try {
      await OffersService.updateImages(updatePayload, offerId);
      enqueueSnackbar('Offer gallery order updated', { variant: 'success' });
    } catch (error) {
      if (error instanceof Error) {
        reportError(error);
      }
      enqueueSnackbar(getErrorMessage(error, 'Failed to update offer gallery order'), { variant: 'error' });
      throw error;
    }
  };

  const reorderRoomImages = async (roomKey: string, orderedPublicIds: Array<string>) => {
    const room = findPersistedRoomByKey(propertyRoomTypes, roomKey);
    const propertyId = room?.property_id;
    const roomTypeId = room?.id;

    if (!propertyId || !roomTypeId) {
      return;
    }

    const persistedUploadedRoomImages = mediaImages.flatMap((image) => {
      if (image.sourceRoomId !== roomKey || image.sourceType !== 'room' || !canPersistRoomImageHiddenState(image)) {
        return [];
      }

      return [
        {
          id: image.sourceImageId,
          id_cloudinary_external: image.publicId,
        },
      ];
    });
    const updatePayload = buildRoomImageOrderUpdatePayload(
      (room.images ?? []).concat(persistedUploadedRoomImages),
      orderedPublicIds,
    );

    if (updatePayload.length === 0) {
      return;
    }

    try {
      await ReservationService.updateRoomTypeImages(updatePayload, propertyId, roomTypeId);
      setRoomImageOrderOverrides((currentOverrides) => ({
        ...currentOverrides,
        [roomKey]: orderedPublicIds,
      }));
      enqueueSnackbar('Room image order updated', { variant: 'success' });
    } catch (error) {
      if (error instanceof Error) {
        reportError(error);
      }
      enqueueSnackbar(getErrorMessage(error, 'Failed to update room image order'), { variant: 'error' });
      throw error;
    }
  };

  const saveRoomImageTitleEdits = async (roomKey: string) => {
    const room = findPersistedRoomByKey(propertyRoomTypes, roomKey);
    const editedRoomImages = mediaImages.flatMap((image) => {
      const imageSourceKey = getMediaImageSourceKey(image);
      const imageEdit = imageEdits[imageSourceKey];
      const nextTitle = imageEdit?.title ?? '';
      const persistedTitle =
        savedImageTitles[imageSourceKey] ??
        room?.images?.find(
          (sourceImage) =>
            String(sourceImage.id) === image.sourceImageId || sourceImage.id_cloudinary_external === image.publicId,
        )?.title ??
        '';

      if (
        image.sourceRoomId !== roomKey ||
        !imageEdit ||
        !canPersistRoomImageHiddenState(image) ||
        nextTitle === persistedTitle
      ) {
        return [];
      }

      return [{ ...image, title: nextTitle }];
    });

    if (editedRoomImages.length === 0) {
      return;
    }

    await Promise.all(
      editedRoomImages.map((image) =>
        ReservationService.updateRoomTypeImage(
          {
            id_cloudinary_external: image.publicId,
            title: image.title,
          },
          image.sourceImageId,
          image.sourcePropertyId,
          image.sourceRoomTypeId,
        ),
      ),
    );

    setSavedImageTitles((currentTitles) => ({
      ...currentTitles,
      ...Object.fromEntries(editedRoomImages.map((image) => [getMediaImageSourceKey(image), image.title])),
    }));
    setImageEdits((currentEdits) => {
      const nextEdits = { ...currentEdits };

      editedRoomImages.forEach((image) => {
        delete nextEdits[getMediaImageSourceKey(image)];
      });

      return nextEdits;
    });
  };

  const deletePersistedRoomImage = (image: MediaImage, successMessage: string, errorMessage: string) => {
    if (!canPersistRoomImageHiddenState(image)) {
      return;
    }

    const imageSourceKey = getMediaImageSourceKey(image);
    const existingDeletionPromise = roomImageDeletionPromisesRef.current.get(imageSourceKey);

    if (existingDeletionPromise) {
      return existingDeletionPromise;
    }

    const deletionPromise = ReservationService.deleteRoomTypeImage(
      image.sourceImageId,
      image.sourcePropertyId,
      image.sourceRoomTypeId,
    )
      .then(() => {
        enqueueSnackbar(successMessage, { variant: 'success' });
      })
      .catch((error) => {
        if (error instanceof Error) {
          reportError(error);
        }
        enqueueSnackbar(getErrorMessage(error, errorMessage), { variant: 'error' });
        throw error;
      })
      .finally(() => {
        roomImageDeletionPromisesRef.current.delete(imageSourceKey);
      });

    roomImageDeletionPromisesRef.current.set(imageSourceKey, deletionPromise);

    return deletionPromise;
  };

  const removeImageFromRoom = async (image: MediaImage) => {
    const persistedRoomImageDeletion = deletePersistedRoomImage(
      image,
      'Room image removed',
      'Failed to remove room image',
    );

    if (persistedRoomImageDeletion) {
      await persistedRoomImageDeletion;
    }

    const imageSourceKey = getMediaImageSourceKey(image);

    setRemovedRoomImageSourceKeys((currentKeys) =>
      currentKeys.includes(imageSourceKey) ? currentKeys : currentKeys.concat(imageSourceKey),
    );
    setHiddenImageOverrides((currentOverrides) => removeImageOverride(currentOverrides, image.publicId));
  };

  const deleteImage = async (publicId: string) => {
    const image = mediaImages.find((mediaImage) => mediaImage.publicId === publicId);
    const persistedRoomImageDeletion = image
      ? deletePersistedRoomImage(image, 'Room image deleted', 'Failed to delete room image')
      : undefined;

    if (persistedRoomImageDeletion) {
      await persistedRoomImageDeletion;
    }
    if (image && offerId && image.sourceImageId && !image.sourceRoomId) {
      try {
        await OffersService.deleteImage(offerId, image.sourceImageId);
        enqueueSnackbar('Offer image deleted', { variant: 'success' });
      } catch (error) {
        if (error instanceof Error) {
          reportError(error);
        }
        enqueueSnackbar(getErrorMessage(error, 'Failed to delete offer image'), { variant: 'error' });
        throw error;
      }
    }

    if (image) {
      const imageSourceKey = getMediaImageSourceKey(image);

      setDeletedImageSourceKeys((currentKeys) =>
        currentKeys.includes(imageSourceKey) ? currentKeys : currentKeys.concat(imageSourceKey),
      );
    } else {
      setDeletedImagePublicIds((currentPublicIds) =>
        currentPublicIds.includes(publicId) ? currentPublicIds : currentPublicIds.concat(publicId),
      );
    }
    setHiddenImageOverrides((currentOverrides) => removeImageOverride(currentOverrides, publicId));
    setImageEdits((currentEdits) => {
      const nextEdits = { ...currentEdits };

      delete nextEdits[publicId];

      return nextEdits;
    });
  };

  const toggleHiddenImage = async (publicId: string) => {
    const image = mediaImages.find((mediaImage) => mediaImage.publicId === publicId);

    if (!image) {
      return;
    }

    const persistentImage = canPersistRoomImageHiddenState(image) ? image : undefined;

    if (persistentImage) {
      if (hiddenImageUpdatePublicIdsRef.current.has(publicId)) {
        return;
      }

      hiddenImageUpdatePublicIdsRef.current.add(publicId);
    }

    const previousHiddenOverride = hiddenImageOverrides[publicId];
    const nextHidden = !(previousHiddenOverride ?? image.hidden ?? false);

    setHiddenImageOverrides((currentOverrides) => ({
      ...currentOverrides,
      [publicId]: nextHidden,
    }));

    if (!persistentImage) {
      return;
    }

    try {
      await ReservationService.updateRoomTypeImage(
        {
          id_cloudinary_external: persistentImage.publicId,
          hidden: nextHidden,
        },
        persistentImage.sourceImageId,
        persistentImage.sourcePropertyId,
        persistentImage.sourceRoomTypeId,
      );
      enqueueSnackbar(nextHidden ? 'Room image hidden' : 'Room image shown', { variant: 'success' });
    } catch (error) {
      setHiddenImageOverrides((currentOverrides) =>
        previousHiddenOverride === undefined
          ? removeImageOverride(currentOverrides, publicId)
          : { ...currentOverrides, [publicId]: previousHiddenOverride },
      );
      if (error instanceof Error) {
        reportError(error);
      }
      enqueueSnackbar(getErrorMessage(error, 'Failed to update room image visibility'), { variant: 'error' });
    } finally {
      hiddenImageUpdatePublicIdsRef.current.delete(publicId);
    }
  };

  const editRoomImage = (publicId: string) => {
    const image =
      mediaImages.find(
        (mediaImage) => mediaImage.publicId === publicId && canPersistRoomImageHiddenState(mediaImage),
      ) ?? mediaImages.find((mediaImage) => mediaImage.publicId === publicId);

    if (image) {
      setRoomImagePendingEdit(image);
    }
  };

  const removeRoomImageFromEditor = (image: MediaImage) => {
    setRoomImagePendingDeletion(image);
  };

  const saveImageEdit = async (publicId: string, edit: LocalImageEdit) => {
    const image =
      roomImagePendingEdit?.publicId === publicId
        ? roomImagePendingEdit
        : mediaImages.find((mediaImage) => mediaImage.publicId === publicId);

    if (!image) {
      return;
    }

    const shouldUploadImage = hasImagePixelEdit(edit);
    let nextPublicId = publicId;

    if (shouldUploadImage) {
      try {
        nextPublicId = await uploadEditedImage(publicId, edit);
      } catch (error) {
        if (error instanceof Error) {
          reportError(error);
        }
        enqueueSnackbar(getErrorMessage(error, 'Failed to save image edits'), { variant: 'error' });
        throw error;
      }
    }

    const persistedImageUpdate = canPersistRoomImageHiddenState(image)
      ? {
          promise: ReservationService.updateRoomTypeImage(
            {
              id_cloudinary_external: nextPublicId,
              title: edit.title,
            },
            image.sourceImageId,
            image.sourcePropertyId,
            image.sourceRoomTypeId,
          ),
          errorMessage: 'Failed to update room image',
          successMessage: 'Room image updated',
        }
      : image.sourceType === 'offer' && image.sourceImageId
        ? {
            promise: OffersService.updateImage(offerId, image.sourceImageId, {
              id_cloudinary_external: nextPublicId,
              order: image.order,
              title: edit.title,
            }),
            errorMessage: 'Failed to update image',
            successMessage: 'Image updated',
          }
        : undefined;

    if (persistedImageUpdate) {
      try {
        await persistedImageUpdate.promise;
        enqueueSnackbar(persistedImageUpdate.successMessage, { variant: 'success' });
      } catch (error) {
        if (error instanceof Error) {
          reportError(error);
        }
        enqueueSnackbar(getErrorMessage(error, persistedImageUpdate.errorMessage), { variant: 'error' });
        throw error;
      }
    }

    if (nextPublicId !== publicId) {
      const nextImage = {
        ...image,
        publicId: nextPublicId,
        title: edit.title || undefined,
      };
      const replacedImageSourceKey = getMediaImageSourceKey(image);

      setReplacedImageSourceKeys((currentKeys) =>
        currentKeys.includes(replacedImageSourceKey) ? currentKeys : currentKeys.concat(replacedImageSourceKey),
      );
      setImagePublicIdReplacements((currentReplacements) => ({
        ...currentReplacements,
        [publicId]: nextPublicId,
      }));
      setUploadedImages((currentImages) => dedupeMediaImages(currentImages.concat(nextImage)));
      setHiddenImageOverrides((currentOverrides) => {
        const previousOverride = currentOverrides[publicId];

        if (previousOverride === undefined) {
          return currentOverrides;
        }

        return {
          ...removeImageOverride(currentOverrides, publicId),
          [nextPublicId]: previousOverride,
        };
      });
      setImageEdits((currentEdits) => {
        const nextEdits = { ...currentEdits };

        delete nextEdits[publicId];

        nextEdits[nextPublicId] = buildDefaultEdit(nextImage);

        return nextEdits;
      });
      return;
    }

    setImageEdits((currentEdits) => ({
      ...currentEdits,
      [publicId]: edit,
    }));
  };

  const updateImageTitle = (roomKey: string, publicId: string, title: string) => {
    const image = mediaImages.find(
      (mediaImage) => mediaImage.publicId === publicId && mediaImage.sourceRoomId === roomKey,
    );

    if (!image) {
      return;
    }

    const imageSourceKey = getMediaImageSourceKey(image);

    setImageEdits((currentEdits) => ({
      ...currentEdits,
      [imageSourceKey]: {
        ...(currentEdits[imageSourceKey] ?? buildDefaultEdit(image)),
        title,
      },
    }));
  };

  const closeRoomImageDeleteDialog = () => {
    setRoomImagePendingDeletion(undefined);
  };

  const confirmRemoveRoomImage = async () => {
    if (!roomImagePendingDeletion) {
      return;
    }

    try {
      await removeImageFromRoom(roomImagePendingDeletion);
    } catch {
      return;
    }

    setRoomImagePendingEdit((currentImage) =>
      currentImage?.publicId === roomImagePendingDeletion.publicId ? undefined : currentImage,
    );
    setRoomImagePendingDeletion(undefined);
  };

  return {
    deletedImagePublicIds,
    hiddenImagePublicIds,
    imageEdits,
    imagePublicIdReplacements,
    isUploadingImages,
    mediaImages,
    roomImagePendingDeletion,
    roomImageOrderOverrides,
    roomImagePendingEdit,
    uploadedRoomAssignments,
    videoId,
    closeRoomImageDeleteDialog,
    confirmRemoveRoomImage,
    deleteImage,
    removeRoomImageFromEditor,
    removeImageFromRoom,
    reorderOfferGalleryImages,
    reorderRoomImages,
    saveRoomImageTitleEdits,
    saveImageEdit,
    setRoomImagePendingEdit,
    setVideoId: setLocalVideoId,
    toggleHiddenImage,
    updateImageTitle,
    uploadFiles,
    uploadFilesToOfferGallery,
    uploadFilesToRoom,
    editRoomImage,
  };
}

function findPersistedRoomByAssignment(
  propertyRoomTypes: Array<PropertyRoomTypeWithImages> | undefined,
  roomAssignment: UploadRoomAssignment,
) {
  return findPersistedRoomByKey(propertyRoomTypes, roomAssignment.roomId);
}

function findPersistedRoomByKey(propertyRoomTypes: Array<PropertyRoomTypeWithImages> | undefined, roomKey: string) {
  return propertyRoomTypes?.find((room, index) => getRoomKey(room, index) === roomKey && room.id);
}

async function createPersistedRoomImage({
  propertyRoomTypes,
  publicId,
  roomAssignment,
  uploadIndex,
}: {
  propertyRoomTypes: Array<PropertyRoomTypeWithImages> | undefined;
  publicId: string;
  roomAssignment: UploadRoomAssignment;
  uploadIndex: number;
}) {
  const room = findPersistedRoomByAssignment(propertyRoomTypes, roomAssignment);
  const propertyId = room?.property_id;
  const roomTypeId = room?.id;

  if (!propertyId || !roomTypeId) {
    return undefined;
  }

  const roomImage = await ReservationService.createRoomTypeImage(
    {
      hidden: false,
      id_cloudinary_external: publicId,
      order: (room.images?.length ?? 0) + uploadIndex + 1,
    },
    propertyId,
    roomTypeId,
  );

  return { ...roomImage, sourcePropertyId: propertyId, sourceRoomTypeId: roomTypeId };
}

async function uploadEditedImage(publicId: string, edit: LocalImageEdit) {
  const sourceFile = await fetchImageFile(publicId, edit.filename);
  const dimensions = await getImageDimensions(sourceFile);
  const croppedFile = await createCroppedImageFile({
    adjustments: {
      brightness: edit.brightness,
      contrast: edit.contrast,
      warmth: edit.warmth,
    },
    croppedAreaPixels: getImageCropPixels(edit, dimensions),
    file: sourceFile,
    outputFileName: edit.filename,
  });
  const uploadFile = await normalizeImageFileForUpload({
    file: croppedFile,
    maxBytes: MAX_SVC_IMAGE_UPLOAD_BYTES,
  });

  const response = await uploadImage(uploadFile);
  const uploadedImageId = getUploadedImageId(response);

  if (!uploadedImageId) {
    throw new Error('Image service did not return an image id');
  }

  return uploadedImageId;
}

async function fetchImageFile(publicId: string, filename: string) {
  const response = await fetch(`${window.configs.IMAGE_HOST}/${publicId}`);

  if (!response.ok) {
    throw new Error('Failed to load image for editing');
  }

  const blob = await response.blob();

  return new File([blob], filename, {
    type: blob.type || 'image/jpeg',
    lastModified: Date.now(),
  });
}
